/**
 * The Two Milks mark: the characters "×2" set in Archivo 800 at -0.055em
 * tracking, optically centred on a rounded square.
 *
 * The path below is NOT drawn by hand. It is the real glyph outline lifted
 * from the Archivo variable font instanced at weight 800 — the brand spec is
 * explicit that the mark is type, and that redrawing it always goes wrong.
 * Regenerate it from the font if it ever needs to change; never edit it here.
 *
 * Inline SVG rather than a styled <span> of live text, for one reason that
 * matters: a text mark renders in the fallback face until Archivo loads, so
 * the logo would visibly reflow on every cold load. An outline is correct from
 * the first paint.
 *
 * The 96-unit viewBox is the spec's own grid, where the corner radius is 26.
 */
const MARK_PATH =
  'M27.63 62.60 23.98 58.93 30.98 51.96 23.98 44.98 27.61 41.34 34.60 48.34 41.60 41.32 45.24 44.98 38.20 51.98 45.22 58.93 41.58 62.58 34.60 55.58Z M48.71 63.44V61.62Q48.71 59.70 49.46 58.06Q50.21 56.41 51.49 54.99Q52.77 53.57 54.33 52.29Q55.89 51.00 57.52 49.80Q59.32 48.46 60.91 47.22Q62.49 45.99 63.49 44.63Q64.48 43.27 64.48 41.59Q64.48 40.71 64.06 39.89Q63.64 39.07 62.74 38.54Q61.85 38.00 60.34 38.00Q58.83 38.00 57.81 38.54Q56.79 39.08 56.26 40.06Q55.73 41.04 55.73 42.38V43.68H49.00Q48.95 43.44 48.93 43.11Q48.91 42.77 48.91 42.35Q48.91 39.15 50.37 36.99Q51.83 34.82 54.52 33.69Q57.21 32.56 60.89 32.56Q63.73 32.56 65.78 33.32Q67.83 34.07 69.16 35.35Q70.48 36.64 71.11 38.25Q71.75 39.87 71.75 41.58Q71.75 43.66 71.01 45.34Q70.27 47.02 68.94 48.49Q67.61 49.95 65.85 51.31Q64.10 52.66 62.06 54.05Q61.02 54.76 60.22 55.33Q59.42 55.91 58.92 56.35Q58.41 56.79 58.19 57.16H72.02V63.44Z';

/** Corner radius, held at the spec's ratio: 16→5, 28→8, 48→13, 96→26. */
const radiusFor = (size: number) => Math.round(size * (26 / 96));

interface LogoProps {
  size?: number;
  /** The square. Defaults to cocoa — the full-colour treatment. */
  background?: string;
  /** The characters. Defaults to cream. */
  color?: string;
  /**
   * Accessible name. Omit it wherever the mark sits next to the wordmark, so
   * a screen reader announces "Two Milks" once rather than twice.
   */
  title?: string;
}

export function Logo({
  size = 28,
  background = 'var(--cocoa)',
  color = 'var(--on-cocoa)',
  title,
}: LogoProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 96 96"
      role={title ? 'img' : 'presentation'}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      style={{ flex: 'none' }}
    >
      {title ? <title>{title}</title> : null}
      <rect width="96" height="96" rx={radiusFor(96)} fill={background} />
      <path d={MARK_PATH} fill={color} />
    </svg>
  );
}

/**
 * The mark reversed, for cocoa and aisle surfaces: cream square, cocoa
 * characters. Used in the footer of an inverted panel and on the share card.
 */
export function LogoReversed({ size = 28, title }: { size?: number; title?: string }) {
  return <Logo size={size} background="var(--on-cocoa)" color="var(--cocoa)" title={title} />;
}
